import React, { useState, useEffect, useRef } from "react";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaLinkedinIn,
  FaUser,
  FaComment,
  FaPaperPlane,
  FaCheckCircle,
  FaGlobeAsia,
  FaHeadset,
  FaWhatsapp,
  FaMapPin,
  FaArrowRight,
} from "react-icons/fa";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: "",
  });

  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [visitorCount, setVisitorCount] = useState(0);

  useEffect(() => {
    const currentSection = sectionRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (currentSection) observer.observe(currentSection);

    return () => {
      if (currentSection) observer.unobserve(currentSection);
    };
  }, []);

  useEffect(() => {
    let count = localStorage.getItem("visitorCount");
    count = count ? parseInt(count, 10) + 1 : 1234;
    localStorage.setItem("visitorCount", count);
    setVisitorCount(count);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        submitted: true,
        success: false,
        message: "Please fill all required fields.",
      });
      return;
    }

    setFormStatus({
      submitted: true,
      success: true,
      message: "Thank you! We'll get back soon.",
    });

    setTimeout(() => {
      setFormStatus({
        submitted: false,
        success: false,
        message: "",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    }, 5000);
  };

  const contactInfo = [
    {
      icon: <FaPhone />,
      title: "Phone",
      details: ["+918318700018"],
      action: "Call",
      link: "tel:+918318700018",
    },
    {
      icon: <FaEnvelope />,
      title: "Email",
      details: ["directors@aabharan.org"],
      action: "Mail",
      link: "mailto:directors@aabharan.org",
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Address",
      details: [
        "Naibazar Chauraha, Durga Puja Road",
        "Sakaldiha, Chandaui, UP - 232108",
      ],
      action: "Directions",
      link: "https://maps.google.com",
    },
    {
      icon: <FaClock />,
      title: "Hours",
      details: ["Mon-Sat: 10AM-6PM", "Sunday: Closed"],
      action: "Visit",
      link: "#",
    },
  ];

  const socialLinks = [
    { icon: <FaFacebookF />, name: "Facebook", link: "#" },
    { icon: <FaTwitter />, name: "Twitter", link: "#" },
    { icon: <FaInstagram />, name: "Instagram", link: "#" },
    { icon: <FaYoutube />, name: "YouTube", link: "#" },
    { icon: <FaLinkedinIn />, name: "LinkedIn", link: "#" },
    { icon: <FaWhatsapp />, name: "WhatsApp", link: "#" },
  ];

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-br from-[#eafaff] via-white to-[#dff7fb] py-14 px-4 sm:px-6 lg:px-8"
    >
      <div className="absolute top-10 left-0 w-72 h-72 bg-[#1298b5]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-0 w-80 h-80 bg-[#f97316]/10 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto">
        <div
          className={`text-center mb-10 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <span className="inline-block bg-white text-[#1298b5] border border-[#bdeef5] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 shadow-sm">
            Get In Touch
          </span>

          <h2 className="text-3xl md:text-5xl font-extrabold text-[#073b4c] mb-3">
            Contact <span className="text-[#1298b5]">Us</span>
          </h2>

          <p className="text-gray-600 max-w-xl mx-auto">
            Have questions? We'd love to hear from you.
          </p>

          <div className="w-20 h-1 bg-gradient-to-r from-[#1298b5] to-[#f97316] mx-auto rounded-full mt-4"></div>
        </div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 transition-all duration-700 delay-100 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {contactInfo.map((item, index) => (
            <div
              key={index}
              className="group bg-white/90 backdrop-blur-sm rounded-2xl shadow-md hover:shadow-xl border border-[#d8f4f8] hover:border-[#1298b5]/50 transition-all duration-300 p-5 hover:-translate-y-1"
            >
              <div className="w-11 h-11 bg-[#eafaff] rounded-xl flex items-center justify-center text-[#1298b5] text-xl mb-3 group-hover:bg-[#1298b5] group-hover:text-white transition-all">
                {item.icon}
              </div>

              <h3 className="font-bold text-[#073b4c] text-base mb-1">
                {item.title}
              </h3>

              {item.details.map((detail, i) => (
                <p key={i} className="text-xs text-gray-600 leading-relaxed">
                  {detail}
                </p>
              ))}

              <a
                href={item.link}
                className="inline-flex items-center gap-1 text-xs text-[#1298b5] hover:text-[#f97316] mt-3 font-semibold group/link"
              >
                {item.action}
                <FaArrowRight className="text-[10px] group-hover/link:translate-x-1 transition" />
              </a>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div
            className={`bg-white/95 rounded-3xl shadow-xl p-6 border border-[#d8f4f8] transition-all duration-700 delay-200 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-10"
            }`}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 bg-[#eafaff] rounded-xl flex items-center justify-center text-[#1298b5]">
                <FaComment />
              </div>

              <div>
                <h3 className="text-xl font-bold text-[#073b4c]">
                  Send Message
                </h3>
                <p className="text-xs text-gray-500">We reply within 24 hrs</p>
              </div>
            </div>

            {formStatus.submitted && (
              <div
                className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
                  formStatus.success
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                <FaCheckCircle
                  className={
                    formStatus.success ? "text-green-500" : "text-red-500"
                  }
                />
                <p>{formStatus.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#073b4c] mb-1">
                  Name *
                </label>

                <div className="relative">
                  <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1298b5] text-sm" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2.5 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none"
                    placeholder="Your name"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-[#073b4c] mb-1">
                    Email *
                  </label>

                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1298b5] text-sm" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-2.5 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none"
                      placeholder="email@ex.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#073b4c] mb-1">
                    Phone
                  </label>

                  <div className="relative">
                    <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1298b5] text-sm" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-2.5 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none"
                      placeholder="+918318700018"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#073b4c] mb-1">
                  Subject
                </label>

                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none"
                  placeholder="Subject"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#073b4c] mb-1">
                  Message *
                </label>

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2.5 border border-[#d8f4f8] rounded-xl focus:ring-2 focus:ring-[#1298b5]/30 focus:border-[#1298b5] outline-none resize-none"
                  placeholder="Your message..."
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#1298b5] to-[#0b819b] hover:from-[#f97316] hover:to-[#fb923c] text-white py-3 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              >
                <FaPaperPlane />
                Send Message
              </button>
            </form>
          </div>

          <div
            className={`space-y-4 transition-all duration-700 delay-300 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-10"
            }`}
          >
            <div className="bg-white/95 rounded-3xl shadow-xl p-5 border border-[#d8f4f8]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#eafaff] rounded-xl flex items-center justify-center text-[#1298b5]">
                  <FaMapPin />
                </div>

                <h3 className="font-bold text-[#073b4c]">Our Location</h3>
              </div>

              <div className="rounded-2xl overflow-hidden h-52 border border-[#d8f4f8]">
                <iframe
                  src="https://www.google.com/maps?q=Naibazar+Sakaldiha+Chandaui+UP+232108&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Office Location"
                  className="w-full h-full object-cover"
                ></iframe>
              </div>

              <p className="text-xs text-gray-600 mt-3 flex items-center gap-1">
                <FaMapMarkerAlt className="text-[#1298b5]" />
                Naibazar, Durga Puja Road, Sakaldiha, Chandaui, Uttar Pradesh -
                232108
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#1298b5] to-[#0b819b] rounded-3xl shadow-xl p-5 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaGlobeAsia />
                </div>

                <h3 className="font-bold">Connect With Us</h3>
              </div>

              <div className="flex flex-wrap gap-2">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.link}
                    className="w-9 h-9 bg-white/20 hover:bg-[#f97316] rounded-xl flex items-center justify-center text-white transition"
                    aria-label={social.name}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            <a
              href="#"
              className="block bg-white/95 rounded-3xl shadow-xl p-5 hover:shadow-2xl transition group border border-[#d8f4f8]"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#eafaff] rounded-xl flex items-center justify-center text-[#1298b5]">
                    <FaHeadset />
                  </div>

                  <div>
                    <h4 className="font-bold text-[#073b4c]">FAQs</h4>
                    <p className="text-sm text-gray-500">
                      Quick answers to common questions
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="bg-[#eafaff] text-[#1298b5] px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                    {formatNumber(visitorCount)} Visitors
                  </div>

                  <FaArrowRight className="text-gray-400 group-hover:text-[#f97316] group-hover:translate-x-1 transition" />
                </div>
              </div>
            </a>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-gray-500">
          © Aabharan Foundation - Serving Communities Since 2023
        </div>
      </div>
    </section>
  );
};

export default ContactSection;